// src/hooks/useTelegramAuth.tsx
import { useCallback, useMemo, useRef, useState } from 'react';
import { z } from 'zod';

/**
 * useTelegramAuth — безопасное связывание Telegram аккаунта через BFF.
 *
 * Требуемые BFF:
 *   POST /api/web/telegram/link
 *     Body: { initData: string } // Telegram WebApp initData или payload виджета Login
 *     → 200 { ok:true, linked:boolean }
 *     → 4xx/5xx { ok:false, code:string, message?:string }
 *
 * Никаких токенов/секретов в консоль/DOM. Валидация Zod. Abort/timeout.
 */

const LinkReq = z.object({
  initData: z.string().min(10), // исходная строка от Telegram (подписанный payload)
});
export type TelegramLinkInput = z.infer<typeof LinkReq>;

const OkResp = z.object({
  ok: z.literal(true),
  linked: z.boolean(),
});
const ErrResp = z.object({
  ok: z.literal(false),
  code: z.string().min(1),
  message: z.string().optional(),
});
const UnionResp = z.union([OkResp, ErrResp]);

type State =
  | { status: 'idle' }
  | { status: 'linking' }
  | { status: 'linked'; linked: boolean }
  | { status: 'error'; error: string };

async function withTimeout<T>(p: Promise<T>, ms: number, signal?: AbortSignal): Promise<T> {
  if (typeof AbortController !== 'undefined') {
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), ms);
    const composite = new AbortController();
    const onAbort = () => composite.abort();
    c.signal.addEventListener('abort', onAbort);
    signal?.addEventListener('abort', onAbort);
    try {
      // @ts-expect-error composite.signal в современных рантаймах доступен
      return await Promise.race([p, new Promise<never>((_, rej) => composite.signal.addEventListener('abort', () => rej(new Error('timeout'))))]);
    } finally {
      clearTimeout(t);
      c.signal.removeEventListener('abort', onAbort);
      signal?.removeEventListener('abort', onAbort);
    }
  }
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error('timeout')), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }).catch((e) => { clearTimeout(t); reject(e); });
  });
}

export function useTelegramAuth() {
  const [state, setState] = useState<State>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  const link = useCallback(async (input: TelegramLinkInput) => {
    abortRef.current?.abort();
    abortRef.current = typeof AbortController !== 'undefined' ? new AbortController() : null;

    setState({ status: 'linking' });

    try {
      const payload = LinkReq.parse(input);
      const r = await withTimeout(
        fetch('/api/web/telegram/link', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: abortRef.current?.signal,
        }),
        10000,
        abortRef.current?.signal
      );

      const text = await r.text();
      let data: unknown = null;
      try { data = text ? JSON.parse(text) : null; } catch { /* ignore */ }

      if (!r.ok) {
        const err = ErrResp.safeParse(data);
        const msg = err.success ? (err.data.message ?? err.data.code) : `HTTP_${r.status}`;
        setState({ status: 'error', error: msg });
        return;
      }

      const parsed = UnionResp.safeParse(data);
      if (!parsed.success) {
        setState({ status: 'error', error: 'Некорректный ответ сервера.' });
        return;
      }
      if (parsed.data.ok) {
        setState({ status: 'linked', linked: parsed.data.linked });
      } else {
        setState({ status: 'error', error: parsed.data.message ?? parsed.data.code });
      }
    } catch (e) {
      const message = e instanceof Error ? (e.name === 'AbortError' || e.message === 'timeout' ? 'Превышено время ожидания.' : e.message) : 'Сетевая ошибка.';
      setState({ status: 'error', error: message });
    }
  }, []);

  return useMemo(() => ({
    state,
    link,
    reset,
    isIdle: state.status === 'idle',
    isLinking: state.status === 'linking',
    isLinked: state.status === 'linked' && state.linked,
    isError: state.status === 'error',
  }), [state, link, reset]);
}